import * as _ from 'lodash';
import { ConfigInstanceToken } from '../../interfaces/config.interface';
import { ConfigConstants } from '../../config/config-constants';
import { CustomInjector } from '../../services/custom-injector/custom-injector';
import { ModelOptionsLoaderService } from '../../services/model-options-loader/model-options-loader.service';
import {
    NotificationIdentifierModel,
    ODataNotificationIdentifierOutModel,
} from '../low-level/notification-identifier.model';
import { NotificationModel } from '../low-level/notification.model';
import { RecipientModel } from '../low-level/recipient.model';
import { RecipientListModelOptions } from '../options/recipient-list-model-options';
import { MultilineTextModel } from '../primitive/multiline-text.model';
import { TextModel } from '../primitive/text.model';
import { CompositeModel } from './composite.model';
import { ODataRecipientListOutModel, RecipientListModel } from './recipient-list.model';

export class DataMaintenanceLocationModel extends CompositeModel {
    public notificationIdentifier: NotificationIdentifierModel;
    public name1: TextModel;
    public name2: TextModel;
    public name3: TextModel;
    public name4: TextModel;
    public street: TextModel;
    public city: TextModel;
    public phone: TextModel;
    public custPONo: TextModel;
    public functionalLoc: TextModel;
    public recipients: RecipientListModel;
    public freeText: MultilineTextModel;
    public readonly isInvitro: boolean;
    public constructor(private notificationModel: NotificationModel) {
        super('DataMaintenanceLocationModel');

        const injector = CustomInjector.getInjector();
        const modelOptionsLoader = injector.get(ModelOptionsLoaderService);
        const configService = injector.get(ConfigInstanceToken);
        this.isInvitro = notificationModel.processIndicator === ConfigConstants.PROCESS_INDICATOR.INVITRO;

        this.notificationIdentifier = this.notificationModel.identifier;

        this.name1 = <TextModel>this.set(new TextModel('Name1', notificationModel.address.name1.value));
        modelOptionsLoader.load(this.name1, 'DataMaintenanceContact', 'ContactPersonFirstname');

        this.name2 = <TextModel>this.set(new TextModel('Name2', notificationModel.address.name2.value));
        modelOptionsLoader.load(this.name2, 'DataMaintenanceContact', 'ContactPersonLastname');

        this.name3 = <TextModel>this.set(new TextModel('Name3', notificationModel.address.name3.value));
        modelOptionsLoader.load(this.name3, 'DataMaintenanceContact', 'ContactPersonFirstname');

        this.name4 = <TextModel>this.set(new TextModel('Name4', notificationModel.address.name4.value));
        modelOptionsLoader.load(this.name4, 'DataMaintenanceContact', 'ContactPersonLastname');

        this.street = <TextModel>this.set(new TextModel('Street', notificationModel.address.street.value));
        modelOptionsLoader.load(this.street, 'DataMaintenanceContact', 'Street');

        this.city = <TextModel>this.set(new TextModel('City', notificationModel.address.city.value));
        modelOptionsLoader.load(this.city, 'DataMaintenanceContact', 'City');

        this.freeText = new MultilineTextModel('FreeText');
        modelOptionsLoader.load(this.freeText, 'DataMaintenanceContact', 'FreeText');

        this.phone = <TextModel>this.set(new TextModel('TelNumber', notificationModel.address.phone.value));
        modelOptionsLoader.load(this.phone, 'DataMaintenanceContact', 'Tel');

        this.custPONo = <TextModel>this.set(new TextModel('CustPONo', this.notificationModel.customerPurchaseOrderNr));
        modelOptionsLoader.load(this.custPONo, 'DataMaintenanceContact', 'CustPONo');

        this.functionalLoc = <TextModel>this.set(new TextModel('FunctionalLoc', this.notificationModel.functionalLoc));
        modelOptionsLoader.load(this.functionalLoc, 'DataMaintenanceContact', 'FunctionalLoc');

        const recipientEntries = notificationModel.emailSet.map(
            (email) =>
                new RecipientModel(
                    email.faxEmail.value,
                    ConfigConstants.confirmationServiceReportCommCategory,
                    true,
                    true,
                    true,
                ),
        );
        this.recipients = new RecipientListModel('Email', recipientEntries);
        // since data maintenance is dependent on Confirmation service report (https://evorait.atlassian.net/browse/SHP-5821)
        // 10 is max entries that is allowed from backend and
        // user can add only 9 recipient entries in data maintenance screen as CSE email address gets added in  Confirmation service report screen
        if (configService.getShowDataMaintenanceEnhancement()) {
            const maxEntries = 9;
            this.recipients.options = new RecipientListModelOptions({ ...this.recipients.options, maxEntries });
        }
    }

    public getOutModel(): ODataDataMaintenanceLocationOutModel {
        const outModel = <ODataDataMaintenanceLocationOutModel>super.getOutModel();

        // Multiple email's can be added. Example: Email 1 : abc@gmail.com , Email 2 : xyz@gmail.com
        this.recipients.value.forEach((recipientModel, i) => {
            const key = `Email ${i + 1}`;
            outModel[key] = recipientModel.getOutModel().FaxEmail;
        });

        // we need the value of free text field and replace "\n" with space else "\n" will be printed
        outModel[this.freeText.name] = this.freeText.value.replace(/\n/g, ' ');
        _.merge(outModel, this.notificationIdentifier.getOutModel());

        return outModel;
    }
}

export interface ODataDataMaintenanceLocationOutModel extends ODataNotificationIdentifierOutModel {
    Name1: string;
    Name2: string;
    Name3: string;
    Name4: string;
    Street: string;
    City: string;
    TelNumber: string;
    CustPONo: string;
    FunctionalLoc: string;
    Room: string;
    Recipient: ODataRecipientListOutModel[];
}
